import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Truck, UserCog, DollarSign, Wrench, Package, FileText, AlertTriangle, TrendingUp, TrendingDown } from 'lucide-react';
import api from '../services/api';
import { formatCurrency } from '../utils/format';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart, Legend } from 'recharts';
import { PageHeader } from '../components/PageParts';

const StatCard = ({ icon: Icon, label, value, sub, tone = 'default', onClick }) => {
  const tones = {
    default: 'bg-white dark:bg-neutral-900 border-neutral-200 dark:border-neutral-800',
    red: 'bg-white dark:bg-neutral-900 border-neutral-200 dark:border-neutral-800',
  };
  return (
    <button
      onClick={onClick}
      className={`card-hover text-left w-full rounded-xl border p-5 ${tones[tone]} disabled:opacity-70`}
      disabled={!onClick}
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <p className="text-xs uppercase tracking-wide text-neutral-500 dark:text-neutral-400 font-medium">{label}</p>
          <p className="text-3xl font-bold mt-2">{value}</p>
        </div>
        <div className="h-10 w-10 rounded-lg bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center text-brand">
          <Icon className="h-5 w-5" />
        </div>
      </div>
      {sub && <p className="text-xs text-neutral-500 dark:text-neutral-400">{sub}</p>}
    </button>
  );
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [charts, setCharts] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, c] = await Promise.all([api.get('/dashboard/stats'), api.get('/dashboard/charts')]);
        setStats(s.data);
        setCharts(c.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading || !stats) {
    return (
      <div>
        <PageHeader title="Dashboard" subtitle="Visão geral do sistema" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-32 rounded-xl shimmer" />
          ))}
        </div>
      </div>
    );
  }

  const revenueData = charts?.monthly || [];

  return (
    <div>
      <PageHeader title="Dashboard" subtitle="Visão geral do sistema" />

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard icon={Users} label="Clientes" value={stats.clients.total} sub={`${stats.clients.active} ativos`} onClick={() => navigate('/clientes')} />
        <StatCard icon={Truck} label="Veículos" value={stats.vehicles.total} sub={`${stats.vehicles.active} ativos • ${stats.vehicles.maintenance} em manutenção`} onClick={() => navigate('/frota')} />
        <StatCard icon={UserCog} label="Funcionários" value={stats.employees.total} sub={`${stats.employees.active} ativos`} />
        <StatCard icon={DollarSign} label="Contas a Receber" value={formatCurrency(stats.receivables.amount)} sub={`${stats.receivables.total} lançamentos`} />
        <StatCard icon={FileText} label="Contas a Pagar" value={formatCurrency(stats.payables.amount)} sub={`${stats.payables.total} lançamentos`} />
        <StatCard icon={Wrench} label="Manutenções" value={stats.maintenance.open} sub={`${stats.maintenance.in_progress} em execução`} />
        <StatCard icon={Package} label="Estoque" value={stats.stock.total_items} sub={`${stats.stock.low_stock} itens em baixa`} />
        <StatCard icon={AlertTriangle} label="Licenciamentos" value={stats.licensing.expiring} sub={`${stats.licensing.expired} vencidos`} />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="lg:col-span-2 rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-semibold">Receita x Despesas</h3>
              <p className="text-xs text-neutral-500">Comparação mensal do ano corrente</p>
            </div>
            <div className="flex gap-4 text-xs">
              <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{background:'#C52429'}}/>Receita</span>
              <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-neutral-400"/>Despesas</span>
            </div>
          </div>
          <div style={{width:'100%', height:280}}>
            <ResponsiveContainer>
              <AreaChart data={revenueData} margin={{ top: 5, right: 5, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id="cr" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#C52429" stopOpacity={0.28}/>
                    <stop offset="95%" stopColor="#C52429" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="ce" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#737373" stopOpacity={0.22}/>
                    <stop offset="95%" stopColor="#737373" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#999" />
                <YAxis tick={{ fontSize: 11 }} stroke="#999" tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => formatCurrency(v)} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                <Area type="monotone" dataKey="revenue" stroke="#C52429" fill="url(#cr)" strokeWidth={2} />
                <Area type="monotone" dataKey="expenses" stroke="#737373" fill="url(#ce)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5">
          <h3 className="font-semibold mb-1">Fluxo de Caixa</h3>
          <p className="text-xs text-neutral-500 mb-4">Saldo mensal</p>
          <div style={{width:'100%', height:280}}>
            <ResponsiveContainer>
              <LineChart data={revenueData} margin={{ top: 5, right: 5, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#999" />
                <YAxis tick={{ fontSize: 11 }} stroke="#999" tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => formatCurrency(v)} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                <Line type="monotone" dataKey="cash_flow" stroke="#C52429" strokeWidth={2.5} dot={{ r: 3, fill: '#C52429' }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Vehicle costs */}
      <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold">Gastos por Veículo</h3>
            <p className="text-xs text-neutral-500">Top 5 veículos com maiores gastos operacionais</p>
          </div>
        </div>
        <div style={{width:'100%', height:260}}>
          <ResponsiveContainer>
            <BarChart data={charts?.vehicle_costs || []} margin={{ top: 5, right: 20, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
              <XAxis dataKey="plate" tick={{ fontSize: 12 }} stroke="#999" />
              <YAxis tick={{ fontSize: 11 }} stroke="#999" tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => formatCurrency(v)} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
              <Bar dataKey="cost" fill="#C52429" radius={[6,6,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
